import createDonutChartOptions, { ChartDatum } from 'lib/agCharts/donutChart';
import { EventWaitingLine } from 'lib/graphql/entities/waitingLines/types';
import { useGetAllWaitingLinesFinancialDataQuery } from 'lib/graphql/graphql';
import { mergePaginatedList, usePagination } from 'lib/graphql/pagination';
import { Languages } from 'lib/i18n/i18n';
import useEvent from 'providers/event/useEvent';
import LanguageContext from 'providers/i18n/LanguageContext';
import { useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ChartCard from '../ChartCard/ChartCard';

interface TotalTransactionsChartProps {
  waitingLinesMap: Record<string, EventWaitingLine>
}

function TotalTransactionsChart(props: TotalTransactionsChartProps) {
  const { waitingLinesMap } = props;

  const { id: eventId, languages } = useEvent();
  const { language } = useContext(LanguageContext);

  const { formatMessage } = useIntl();

  const {
    data,
    loading,
    hasNextPage,
  } = usePagination(
    useGetAllWaitingLinesFinancialDataQuery({ variables: { eventId } }),
    (d) => d.getAllWaitingLinesFinancialData.lastEvaluated,
    (existing, incoming) => ({
      getAllWaitingLinesFinancialData: mergePaginatedList(
        existing.getAllWaitingLinesFinancialData,
        incoming.getAllWaitingLinesFinancialData,
        'items',
      ),
    }),
  );
  const aggregates = data?.getAllWaitingLinesFinancialData.items;

  const defaultLanguage = languages[0] as Languages;
  const chartOptions = useMemo(() => {
    if (aggregates) {
      const chartData = aggregates.map<ChartDatum>((aggregate) => {
        const name = waitingLinesMap[aggregate.waitingLineId]?.name || { [defaultLanguage]: '' };
        return {
          amount: aggregate.transactionCount,
          asset: (name[language] || name[defaultLanguage]) as string,
        };
      });
      return createDonutChartOptions(chartData, language, formatMessage({ id: 'transactions' }));
    }
    return {};
  }, [aggregates, defaultLanguage, formatMessage, language, waitingLinesMap]);

  return (
    <ChartCard
      title={<FormattedMessage id="total_transactions" />}
      loading={loading || hasNextPage}
      chartOptions={chartOptions}
    />
  );
}

export default TotalTransactionsChart;
